"use client";

import { useId, useRef, type ClipboardEvent, type KeyboardEvent, type ReactNode, type Ref } from "react";
import { Knopf } from "../knopf";
import { useDateiZiehen } from "./datei-ablage";

/**
 * Chat-Eingabe (Musterkatalog M-094, Design-Guide `chat-eingabe.md`): Feld für eine Frage an den Wissens-Assistenten, nach der
 * Eingabe des Blocks `threaded-ai-chat-workspace-with-sources-sidebar` (Fläche `bg-layer border-line-3 rounded-2xl`, Feld ohne
 * Rand, Knopfreihe darunter: links Anhängen und Werkzeuge, rechts Diktat und Senden). Abweichend vom Block ist Senden ein
 * KI-Auslöser in der Segment-Form des Knopfs (W183 A + B: Sparkles, `ai-gated ai-mark`) statt des runden Primärknopfs.
 *
 * Bedienung: Eingabe sendet, Umschalt+Eingabe bricht um (IME-Komposition wird nicht unterbrochen); `beiTaste` darf vorher eine
 * Taste übernehmen (z. B. Tab für einen Vorschlag) und gibt dann `true` zurück. Dateien kommen über den Knopf (verstecktes
 * natives Feld, Sperrklinke R14 hier im Baustein), Ziehen über die Fläche (`useDateiZiehen`) und Einfügen (`beimEinfuegen`).
 * Das Feld wächst mit dem Inhalt bis `max-h-36` (`field-sizing-content`), dann scrollt es.
 *
 * Der Baustein prüft weder Typ, Größe noch Rechte: die Chat-Route prüft Sitzung, Modul-Recht und KI-Gate selbst. Bei KI aus
 * rendert der Aufrufer die Eingabe nicht, sondern den Ersatzsatz (R-D). Fehler am Feld (`hinweis`) liefert der Aufrufer als Satz.
 */
const FLAECHE = "bg-layer border border-line-3 rounded-2xl shadow-xs";
const FLAECHE_ZIEHEND = "bg-layer border border-dashed border-primary rounded-2xl shadow-xs";
const FELD =
  "field-sizing-content max-h-36 mt-2 pt-4 pb-2 ps-2 pe-4 block w-full bg-transparent border-transparent resize-none text-foreground placeholder:text-muted-foreground-1 focus:outline-hidden focus:border-transparent focus:ring-transparent disabled:opacity-50 disabled:pointer-events-none overflow-y-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:rounded-none [&::-webkit-scrollbar-track]:bg-scrollbar-track [&::-webkit-scrollbar-thumb]:bg-scrollbar-thumb";

export function ChatEingabe({
  label,
  platzhalter,
  platzhalterZiehend = "Datei hier ablegen …",
  wert,
  beiWert,
  beiSenden,
  beiStopp,
  laeuft,
  sendenGesperrt,
  bereit = true,
  feldGesperrt = false,
  beiDateien,
  accept,
  anhangSymbol,
  anhangLabel = "Datei anhängen",
  stoppSymbol,
  beimEinfuegen,
  beiTaste,
  werkzeuge,
  diktat,
  anhaenge,
  hinweis,
  feldRef,
}: {
  /** Name des Felds für Hilfstechnik (sr-only, wie im Block). */
  label: string;
  platzhalter: string;
  platzhalterZiehend?: string;
  wert: string;
  beiWert: (wert: string) => void;
  beiSenden: () => void;
  beiStopp: () => void;
  /** Eine Antwort strömt: statt „Senden“ steht „Stopp“, das Feld ist gesperrt. */
  laeuft: boolean;
  /** Anhänge werden vorbereitet: „Senden“ zeigt den Spinner (sichtbarer Grund, r261); sonst bleibt es klickbar, auch bei leerem Feld (dann ohne Wirkung). */
  sendenGesperrt: boolean;
  /** Seite ist hydriert (Standard `true`). `false` (Server-Render, vor der Hydrierung): Feld und „Senden“ sind gesperrt, „Senden“ zeigt den Spinner und der Satz „Wird geladen …“ steht darunter, nichts geht still verloren (r262). */
  bereit?: boolean;
  feldGesperrt?: boolean;
  beiDateien: (dateien: File[]) => void;
  accept: string;
  anhangSymbol: ReactNode;
  anhangLabel?: string;
  stoppSymbol: ReactNode;
  beimEinfuegen?: (e: ClipboardEvent<HTMLTextAreaElement>) => void;
  /** Darf eine Taste vor dem Senden übernehmen; `true` = übernommen. */
  beiTaste?: (e: KeyboardEvent<HTMLTextAreaElement>) => boolean;
  /** Weitere Knöpfe links neben „Datei anhängen“ (z. B. Schalter für die Bildanalyse). */
  werkzeuge?: ReactNode;
  /** Knopf rechts vor „Senden“ (Diktat). */
  diktat?: ReactNode;
  /** Gewählte Anhänge über dem Feld. */
  anhaenge?: ReactNode;
  /** Satz am Feld (Ablehnung oder Fehlschlag, r105), unter der Fläche. */
  hinweis?: ReactNode;
  feldRef?: Ref<HTMLTextAreaElement>;
}) {
  const id = useId();
  const datei = useRef<HTMLInputElement>(null);
  const { ziehend, bereichProps } = useDateiZiehen(beiDateien, !laeuft && !feldGesperrt);

  function taste(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (beiTaste?.(e)) return;
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      if (bereit && !laeuft && !sendenGesperrt) beiSenden();
    }
  }

  return (
    <div className="ai-gated space-y-2" data-ki="eingabe">
      {/* Preline: communication/communication-sub-chat-inbox/communication-cat-ai-chat-layouts/threaded-ai-chat-workspace-with-sources-sidebar */}
      <form
        className={ziehend ? FLAECHE_ZIEHEND : FLAECHE}
        onSubmit={(e) => {
          e.preventDefault();
          if (bereit && !laeuft && !sendenGesperrt) beiSenden();
        }}
        {...bereichProps}
      >
        <label htmlFor={id} className="sr-only">
          {label}
        </label>
        <div className="pb-2 px-2">
          {anhaenge && <div className="pt-2 px-2 flex flex-wrap gap-2">{anhaenge}</div>}
          <textarea
            ref={feldRef}
            id={id}
            rows={1}
            className={FELD}
            placeholder={ziehend ? platzhalterZiehend : platzhalter}
            value={wert}
            disabled={laeuft || feldGesperrt || !bereit}
            onChange={(e) => beiWert(e.target.value)}
            onKeyDown={taste}
            onPaste={beimEinfuegen}
          />
          <div className="pt-2 flex justify-between items-center gap-x-1">
            <div className="flex flex-wrap items-center gap-x-1">
              <input
                ref={datei}
                type="file"
                multiple
                accept={accept}
                className="sr-only"
                tabIndex={-1}
                aria-hidden="true"
                onChange={(e) => {
                  const liste = Array.from(e.target.files ?? []);
                  e.target.value = "";
                  if (liste.length > 0) beiDateien(liste);
                }}
              />
              <Knopf
                variante="ghost"
                nurSymbol
                groesse="sm"
                label={anhangLabel}
                titel={anhangLabel}
                symbol={anhangSymbol}
                disabled={laeuft || feldGesperrt || !bereit}
                onClick={() => datei.current?.click()}
              />
              {werkzeuge}
            </div>
            <div className="flex items-center gap-x-1">
              {diktat}
              {laeuft ? (
                <Knopf groesse="sm" symbol={stoppSymbol} onClick={beiStopp}>
                  Stopp
                </Knopf>
              ) : (
                <Knopf ki type="submit" groesse="sm" laedt={sendenGesperrt || !bereit}>
                  Senden
                </Knopf>
              )}
            </div>
          </div>
        </div>
      </form>
      {!bereit && (
        <p className="text-xs text-muted-foreground-1" role="status" data-testid="eingabe-laedt">
          Wird geladen …
        </p>
      )}
      {hinweis}
    </div>
  );
}
